import { useState } from 'react';

import * as Sentry from '@sentry/react';

import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';

import { tokens } from '../../../theme-next';
import { UploadRejectedError, uploadFileToFirebase } from '../../../utils/firebaseUpload';

import { MultiDropzone } from './MultiDropzone';

interface SimpleUploadZoneProps {
  /** URLs Firebase déjà uploadées. */
  urls: string[];
  /** Remplace la liste des URLs (ajout / suppression). */
  onChange: (urls: string[]) => void;
  /** Préfixe de chemin Firebase (ex : `cg-cotitulaire`, `cg-commentaire`). */
  pathPrefix: string;
  disabled?: boolean;
}

/**
 * Zone d'upload Firebase SANS OCR — les fichiers ne passent PAS par le pipeline
 * d'analyse (`useAutoExtractQueue`). Réutilise `MultiDropzone` (purement
 * présentationnel) pour le drag-and-drop, uploade chaque fichier via
 * `uploadFileToFirebase`, et gère la liste des URLs (ajout + suppression).
 *
 * Utilisée pour les pièces du co-titulaire (déclarées, non classifiées) et les
 * fichiers libres joints au commentaire de l'agence.
 */
export const SimpleUploadZone = ({ urls, onChange, pathPrefix, disabled = false }: SimpleUploadZoneProps) => {
  const [uploadingCount, setUploadingCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleFilesAdded = async (files: File[]) => {
    setError(null);
    setUploadingCount((n) => n + files.length);
    try {
      const uploaded = await Promise.all(files.map((f) => uploadFileToFirebase(f, pathPrefix)));
      onChange([...urls, ...uploaded]);
    } catch (err) {
      // Fichier refusé pour une raison qu'on sait expliquer (trop lourd, format
      // non accepté) : ce n'est PAS un incident, c'est une consigne à donner à
      // l'agence. On l'affiche telle quelle et on n'alerte pas Sentry.
      if (err instanceof UploadRejectedError) {
        setError(err.message);
        return;
      }
      // Retour Biwiz 2026-07 : échecs d'upload (dropzone co-titulaire / PJ
      // commentaire) qui restaient MUETS — le catch nu avalait l'erreur, donc
      // aucun signal Sentry alors que l'agence était bloquée. On remonte
      // désormais l'exception (avec le pathPrefix concerné) pour diagnostiquer
      // notamment les refus Firebase Storage (permission / règles de chemin).
      Sentry.captureException(err, {
        tags: { flow: 'cgia-upload' },
        extra: { pathPrefix, fileCount: files.length },
      });
      setError("Un fichier n'a pas pu être envoyé. Réessayez.");
    } finally {
      setUploadingCount((n) => Math.max(0, n - files.length));
    }
  };

  const removeUrl = (url: string) => onChange(urls.filter((u) => u !== url));

  const fileNameOf = (url: string): string => {
    try {
      const decoded = decodeURIComponent(url.split('?')[0]);
      return decoded.split('/').pop() || 'fichier';
    } catch {
      return 'fichier';
    }
  };

  return (
    <Box>
      <MultiDropzone onFilesAdded={(files) => void handleFilesAdded(files)} disabled={disabled} />

      {uploadingCount > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
          <CircularProgress size={14} thickness={5} />
          <Typography variant="caption" sx={{ color: tokens.color.textMuted }}>
            Envoi de {uploadingCount} fichier{uploadingCount > 1 ? 's' : ''}…
          </Typography>
        </Box>
      )}

      {error && (
        <Typography variant="caption" sx={{ display: 'block', color: tokens.color.red, marginTop: '6px' }}>
          {error}
        </Typography>
      )}

      {urls.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px' }}>
          {urls.map((url) => (
            <Box
              key={url}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 10px',
                borderRadius: `${tokens.radius.md}px`,
                backgroundColor: tokens.color.white,
                border: `0.5px solid ${tokens.color.border}`,
              }}
            >
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: tokens.color.textMuted, flexShrink: 0 }} />
              <Typography
                variant="caption"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontWeight: 700,
                  color: tokens.color.dark,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={fileNameOf(url)}
              >
                {fileNameOf(url)}
              </Typography>
              <IconButton size="small" onClick={() => removeUrl(url)} disabled={disabled} aria-label="Retirer">
                <ClearRoundedIcon sx={{ fontSize: 16, color: tokens.color.textMuted }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SimpleUploadZone;
